<template>
  <div>
    <!-- Header -->
    <div class="mb-6">
      <h3 class="text-lg font-medium text-heading">
        {{ $t('AI_SETTINGS.CONFIGURATIONS.TITLE') }}
      </h3>
      <p class="text-sm text-body-light">
        {{ $t('AI_SETTINGS.CONFIGURATIONS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Single AI Configuration -->
    <div v-if="!isLoading && configuration" class="card p-6">
      <div class="flex items-start justify-between">
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-woot-100 rounded-lg flex items-center justify-center">
              <fluent-icon :icon="providerIcon" size="20" class="text-woot-600" />
            </div>
            <div>
              <h4 class="text-lg font-medium text-heading">
                {{ configuration.provider_display_name }}
              </h4>
              <p class="text-sm text-body-light">
                {{ configuration.model }} • {{ configuration.api_url ? 'Custom URL' : 'Default URL' }}
              </p>
            </div>
          </div>
          
          <!-- Configuration Status -->
          <div class="flex items-center gap-4 mb-4">
            <div
              class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full"
              :class="configuration.has_api_key ? 'text-success-800 bg-success-50' : 'text-warning-800 bg-warning-50'"
            >
              <fluent-icon 
                :icon="configuration.has_api_key ? 'key' : 'key-multiple'" 
                size="12"
              />
              {{ configuration.has_api_key ? $t('AI_SETTINGS.CARD.CONFIGURED') : $t('AI_SETTINGS.CARD.NOT_CONFIGURED') }}
            </div>
            
            <div v-if="configuration.bot_version" class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-woot-500 rounded-full">
              <fluent-icon icon="checkmark-circle" size="12" />
              {{ configuration.bot_version }}
            </div>
          </div>
          
          <!-- Bot Info -->
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-body-light">Bot Name:</span>
                <span class="ml-2 font-medium text-heading">{{ configuration.bot_name || 'Chưa đặt tên' }}</span>
              </div>
              <div>
                <span class="text-body-light">Business:</span>
                <span class="ml-2 font-medium text-heading">{{ configuration.business_name || 'Chưa đặt tên' }}</span>
              </div>
            </div>
        </div>
        
        <!-- Actions -->
        <div class="flex items-center gap-2">
          <woot-button
            variant="clear"
            size="small"
            icon="settings"
            class="hover:text-woot-500"
            @click="editConfiguration"
          />
          
          <woot-button
            variant="clear"
            size="small"
            icon="flash"
            class="hover:text-woot-500"
            @click="testConnection"
          />
          
          <woot-button
            variant="clear"
            size="small"
            icon="delete"
            class="hover:text-danger-500"
            @click="deleteConfiguration"
          />
        </div>
      </div>
    </div>

    <!-- Empty State - Create First Configuration -->
    <div
      v-if="!isLoading && !configuration"
      class="text-center py-12 card"
    >
      <div class="text-body-light mb-4">
        <fluent-icon icon="brain-circuit" size="48" />
      </div>
      <h3 class="text-lg font-medium text-heading mb-2">
        {{ $t('AI_SETTINGS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-body-light mb-6">
        {{ $t('AI_SETTINGS.EMPTY_STATE.MESSAGE') }}
      </p>
      <woot-button
        color-scheme="primary"
        icon="add"
        @click="showCreateModal = true"
      >
        {{ $t('AI_SETTINGS.ADD_CONFIGURATION') }}
      </woot-button>
    </div>

    <!-- Create/Edit Modal -->
    <AiConfigurationModal
      v-if="showCreateModal || showEditModal"
      :show="showCreateModal || showEditModal"
      :configuration="selectedConfiguration"
      :providers="providers"
      @close="closeModal"
      @save="saveConfiguration"
    />

    <!-- Delete Confirmation Modal -->
    <woot-confirm-delete-modal
      v-if="showDeleteModal"
      :show="showDeleteModal"
      :title="$t('AI_SETTINGS.DELETE.TITLE')"
      :message="$t('AI_SETTINGS.DELETE.MESSAGE')"
      :confirm-text="$t('AI_SETTINGS.DELETE.CONFIRM')"
      :reject-text="$t('AI_SETTINGS.DELETE.CANCEL')"
      @on-confirm="confirmDelete"
      @on-reject="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import AiConfigurationModal from './AiConfigurationModal.vue';

export default {
  name: 'AiConfigurationsTab',
  components: {
    AiConfigurationModal,
  },
  
  props: {
    configuration: {
      type: Object,
      default: null
    },
    providers: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh'],
  
  data() {
    return {
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      selectedConfiguration: null,
      configToDelete: null,
    };
  },
  
  computed: {
    providerIcon() {
      const icons = {
        openai: 'brain-circuit',
        claude: 'chat-multiple',
        gemini: 'star',
        custom: 'settings',
      };
      return icons[this.configuration?.provider] || 'settings';
    }
  },
  
  methods: {
    editConfiguration() {
      this.selectedConfiguration = { ...this.configuration };
      this.showEditModal = true;
    },
    
    deleteConfiguration() {
      this.configToDelete = this.configuration;
      this.showDeleteModal = true;
    },
    
    async confirmDelete() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        await window.axios.delete(`/api/v1/accounts/${accountId}/ai_configurations/${this.configToDelete.id}`);
        useAlert(this.$t('AI_SETTINGS.DELETE.SUCCESS'));
        this.$emit('refresh');
      } catch (error) {
        console.error('Error deleting configuration:', error);
        useAlert(this.$t('AI_SETTINGS.DELETE.ERROR'));
      } finally {
        this.showDeleteModal = false;
        this.configToDelete = null;
      }
    },
    
    async testConnection() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const response = await window.axios.post(`/api/v1/accounts/${accountId}/ai_configurations/${this.configuration.id}/test_connection`);
        
        if (response.data.success) {
          useAlert(response.data.message, 'success');
        } else {
          useAlert(response.data.message, 'error');
        }
      } catch (error) {
        console.error('Error testing connection:', error);
        useAlert(this.$t('AI_SETTINGS.TEST.ERROR'));
      }
    },
    
    async saveConfiguration(configData) {
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        
        if (this.selectedConfiguration) {
          // Update existing
          await window.axios.put(
            `/api/v1/accounts/${accountId}/ai_configurations/${this.selectedConfiguration.id}`,
            { ai_configuration: configData }
          );
        } else {
          // Create new
          await window.axios.post(
            `/api/v1/accounts/${accountId}/ai_configurations`,
            { ai_configuration: configData }
          );
        }
        
        useAlert(this.$t('AI_SETTINGS.SAVE.SUCCESS'));
        this.closeModal();
        this.$emit('refresh');
      } catch (error) {
        console.error('Error saving configuration:', error);
        useAlert(this.$t('AI_SETTINGS.SAVE.ERROR'));
      }
    },
    
    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.selectedConfiguration = null;
    },
  },
};
</script>

<style scoped>
.card {
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
}
</style> 